import { type Map } from 'maplibre-gl';
import { SpriteIconData, SpriteIndex } from '@/scripts/types';

/**
 * Load a sprite (PNG + JSON) into MapLibre map using map.addImage()
 */
export async function loadSpriteIntoMap(map: Map, spriteBaseUrl: string): Promise<void> {
  console.log(`[Sprite] Loading sprite from: ${spriteBaseUrl}`);

  try {
    // Detect high-DPI display
    const pixelRatio = window.devicePixelRatio || 1;
    const suffix = pixelRatio >= 2 ? '@2x' : '';

    // Load sprite index and image
    const [index, spriteImage] = await Promise.all([
      fetch(`${spriteBaseUrl}${suffix}.json`).then(r => {
        if (!r.ok) throw new Error(`Failed to load sprite index: ${r.status}`);
        return r.json() as Promise<SpriteIndex>;
      }),
      loadImage(`${spriteBaseUrl}${suffix}.png`),
    ]);

    // Extract each icon from sprite and add to map
    for (const [iconId, iconData] of Object.entries(index)) {
      try {
        const imageData = extractIconFromSprite(spriteImage, iconData);
        map.addImage(iconId, imageData, { sdf: true, pixelRatio: pixelRatio >= 2 ? 2 : 1 });
      } catch (error) {
        console.error(`[Sprite] Failed to add icon "${iconId}":`, error);
      }
    }
  } catch (error) {
    console.error('[Sprite] Error loading sprite:', error);
    throw error;
  }
}

/**
 * Load an image from URL
 */
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}

/**
 * Extract a single icon region from the sprite image
 * Uses high-quality image smoothing for better edge quality
 */
function extractIconFromSprite(spriteImage: HTMLImageElement, iconData: SpriteIconData): ImageData {
  const canvas = document.createElement('canvas');
  canvas.width = iconData.width;
  canvas.height = iconData.height;
  const ctx = canvas.getContext('2d', {
    alpha: true,
    desynchronized: false,
    willReadFrequently: false,
  });

  if (!ctx) {
    throw new Error('Failed to get canvas 2d context');
  }

  // Enable high-quality image smoothing
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high'; // Best quality smoothing

  // Draw the specific icon region from the sprite
  ctx.drawImage(
    spriteImage,
    iconData.x, // Source X
    iconData.y, // Source Y
    iconData.width, // Source width
    iconData.height, // Source height
    0, // Dest X
    0, // Dest Y
    iconData.width, // Dest width
    iconData.height // Dest height
  );

  return ctx.getImageData(0, 0, iconData.width, iconData.height);
}

export function getIconSvgPath(iconId: string): string {
  const parts = iconId.split('-');
  const category = parts[0];
  const name = parts.slice(1).join('-');
  return `/sprites/v1/icons/${category}/${name}.svg`;
}

export const calculateSpriteLayout = (spriteIndex: SpriteIndex | null) => {
  if (!spriteIndex) return null;
  const values = Object.values(spriteIndex);
  if (values.length === 0) return { width: 0, height: 0 };

  const width = Math.max(...values.map(v => v.x + v.width));
  const height = Math.max(...values.map(v => v.y + v.height));
  return { width, height };
};
